import React, {
  FunctionComponent,
  SyntheticEvent,
  useState,
  useEffect,
} from 'react';
import { useApolloClient, useQuery } from '@apollo/react-hooks';
import { useParams } from 'react-router-dom';
import { pathOr } from 'ramda';
import withStyles from 'react-jss';

import FormSelect from 'components/FormSelect';
import { ClassProp } from 'helpers/types';

import styles from './VenueTypeFilter.styles';
import { GET_MERCHANT } from './VenueTypeFilter.query';

type Styles = ClassProp<ReturnType<typeof styles>>;
type Props = {
  classes: Styles;
  count: number;
};

type Merchant = {
  hasOnlineVenue: boolean;
  hasNearbyVenue: boolean;
};

const venueTypes = {
  options: [
    {
      key: 'all',
      text: 'Show All',
      value: null,
    },
    {
      key: 'nearBy',
      text: 'In Store',
      value: 'nearby',
    },
    {
      key: 'online',
      text: 'Online',
      value: 'online',
    },
  ],
  label: {
    children: 'Transaction Type',
  },
  placeholder: 'Show All',
};

const calcFilterCount = (
  currCount: number,
  newValue: string,
  prevValue: string | null,
): number => {
  // return current count if all values null or the filter already has a value
  if ((newValue === null && prevValue === null) || (newValue && prevValue)) {
    return currCount;
  }

  // reduce filter count by one if filter is reset
  if (newValue === null && currCount > 0) {
    return currCount - 1;
  }

  // add one when filter added
  if (!prevValue && newValue) {
    return currCount + 1;
  }

  // otherwise just return the current count
  return currCount;
};

const VenueTypeFilter: FunctionComponent<Props> = ({ classes, count }) => {
  const params = useParams();
  const [venueType, setVenueType] = useState<string | null>(null);
  const client = useApolloClient();

  useEffect(() => {
    client.writeData({ data: { venueType } });
  }, [venueType]);

  const { merchantName }: any = params;
  const { loading, data } = useQuery(GET_MERCHANT, {
    variables: {
      params: {
        merchantName,
      },
    },
  });

  if (loading) return null;
  const { hasNearbyVenue, hasOnlineVenue } = pathOr(
    [],
    ['merchantList', 'result', '0'],
    data,
  ) as Merchant;

  if (!hasNearbyVenue || !hasOnlineVenue) return null;

  const onChange = (_: SyntheticEvent<HTMLElement, Event>, { value }: any) => {
    setVenueType(value);

    const newCount = calcFilterCount(count, value, venueType);
    client.writeData({ data: { filterCount: newCount } });
  };

  return (
    <div className={classes.container}>
      <span className={classes.label}>Transaction Type</span>

      <FormSelect
        fluid
        selectOnBlur={false}
        name="venueType"
        placeholder={venueTypes.placeholder}
        options={venueTypes.options}
        onChange={onChange}
        value={venueType}
      />
    </div>
  );
};

export default withStyles(styles as any)(VenueTypeFilter);
